import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AdminContentTab({
  aboutSection, setAboutSection,
  aboutVideoSource, setAboutVideoSource,
  uploadingAboutVideo, uploadingAboutImage,
  onAboutVideoUpload, onAboutImageUpload, onSaveAbout,
  testimonials,
  onShowTestimonialDialog, onDeleteTestimonial
}) {
  return (
    <>
      <h2 className='text-2xl font-bold text-[#1A3C34] mb-6' style={{ fontFamily: 'Clash Display' }}>Content Management</h2>
      <div className='grid md:grid-cols-2 gap-6'>
        <Card className='bg-white rounded-2xl p-6'>
          <h3 className='text-xl font-bold text-[#1A3C34] mb-4'>About Section</h3>
          <div className='space-y-4'>
            <div>
              <Label>Title</Label>
              <Input 
                value={aboutSection.title} 
                onChange={(e) => setAboutSection({...aboutSection, title: e.target.value})} 
                placeholder='About section title'
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={aboutSection.description} 
                onChange={(e) => setAboutSection({...aboutSection, description: e.target.value})} 
                placeholder='About section description'
                rows={4}
              />
            </div>
            <div>
              <Label className='mb-2 block font-semibold'>Portrait Picture (About Section)</Label>
              <input data-testid='about-image-upload' type='file' accept='image/*' onChange={onAboutImageUpload} disabled={uploadingAboutImage} className='w-full p-2 border rounded' />
              {uploadingAboutImage && <p className='text-sm text-gray-500 mt-2'>Uploading image...</p>}
              {aboutSection.image_file && (
                <div className='mt-2'>
                  <p className='text-sm text-green-600'>Image uploaded: {aboutSection.image_file}</p>
                  <img src={`${API}/about/image/${aboutSection.image_file}`} alt='Portrait preview' className='mt-2 w-32 h-40 object-cover rounded-lg border' />
                </div>
              )}
            </div>
            <hr className='border-gray-200' />
            <div>
              <Label className='mb-2 block font-semibold'>Video (Meet Your Guide Section)</Label>
              <div className='flex gap-4 mb-4'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input type='radio' name='aboutVideoSource' value='youtube' checked={aboutVideoSource === 'youtube'}
                    onChange={(e) => { setAboutVideoSource(e.target.value); setAboutSection({...aboutSection, video_file: '', youtube_url: ''}); }} />
                  <span>YouTube URL</span>
                </label>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input type='radio' name='aboutVideoSource' value='upload' checked={aboutVideoSource === 'upload'}
                    onChange={(e) => { setAboutVideoSource(e.target.value); setAboutSection({...aboutSection, video_file: '', youtube_url: ''}); }} />
                  <span>Upload Video</span>
                </label>
              </div>
              {aboutVideoSource === 'youtube' ? (
                <div>
                  <Label>YouTube URL</Label>
                  <Input value={aboutSection.youtube_url || ''} onChange={(e) => setAboutSection({...aboutSection, youtube_url: e.target.value, video_file: ''})} placeholder='https://www.youtube.com/watch?v=...' />
                </div>
              ) : (
                <div>
                  <Label>Upload Video File</Label>
                  <input type='file' accept='video/*' onChange={onAboutVideoUpload} disabled={uploadingAboutVideo} className='w-full p-2 border rounded' />
                  {uploadingAboutVideo && <p className='text-sm text-gray-500 mt-2'>Uploading...</p>}
                  {aboutSection.video_file && <p className='text-sm text-green-600 mt-2'>Video uploaded: {aboutSection.video_file}</p>}
                </div>
              )}
            </div>
            <Button data-testid='save-about-btn' onClick={onSaveAbout} className='w-full bg-[#1A3C34] text-white rounded-full'>Save About Section</Button>
          </div>
        </Card>

        <Card className='bg-white rounded-2xl p-6'>
          <div className='flex justify-between items-center mb-4'>
            <h3 className='text-xl font-bold text-[#1A3C34]'>Testimonials</h3>
            <Button onClick={onShowTestimonialDialog} className='bg-[#D4F246] text-[#1A3C34] hover:bg-[#E2F585] rounded-full' size='sm'>
              <Plus className='w-4 h-4 mr-2' /> Add Testimonial
            </Button>
          </div>
          <div className='space-y-3 max-h-96 overflow-y-auto'>
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className='border rounded-lg p-3'>
                <div className='flex justify-between items-start mb-2'>
                  <div>
                    <div className='font-medium text-[#1A3C34]'>{testimonial.name}</div>
                    <div className='text-sm text-gray-600'>{testimonial.role}</div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <span className='text-yellow-500'>{'★'.repeat(testimonial.rating)}</span>
                    <Button size='sm' variant='outline' onClick={() => onDeleteTestimonial(testimonial.id)}>
                      <Trash2 className='w-3 h-3' />
                    </Button>
                  </div>
                </div>
                <p className='text-sm text-gray-700'>{testimonial.content}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
